/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import type { ModuleLayoutConfig } from '@coursera-web/cds-labs';
import { ModuleLayout, noImage, unifiedModuleColors } from '@coursera-web/cds-labs';

import Imgix from 'js/components/Imgix';

import { Grid, breakpoints } from '@coursera/cds-core';

import CTAContentful from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentful';
import FooterContentfulText from 'bundles/coursera-plus-landing-page/components/footerContentful/FooterContentfulText';
import { CTA_LOCATIONS } from 'bundles/coursera-plus-landing-page/contentful/constants';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import { useIsRedesignPromoPageEnabled } from 'bundles/coursera-plus-landing-page/utils/useIsRedesignPromoPageEnabled';
import { logo } from 'bundles/coursera-plus/utils/courseraPlusUtils';

import _t from 'i18n!nls/coursera-plus-landing-page';

const styles = {
  contentContainer: css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    grid-gap: var(--cds-spacing-400);
    align-items: center;
    text-align: center;

    a {
      color: white !important;
    }
    ${breakpoints.down('xs')} {
      grid-gap: var(--cds-spacing-300);
    }
  `,
  wrapper: css`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  `,
  ctaContainer: css`
    width: 100%;
  `,
  typography: (showFooterTextOnMobile: boolean) => css`
    max-width: 700px;

    ${breakpoints.down('xs')} {
      ${showFooterTextOnMobile && 'display: none;'}
    }
  `,
};

const CourseraPlusFooter = () => {
  const { landingPage } = useLandingPageExtended();
  const { showFooterTextOnMobile, heroCtaPartsCollection } = landingPage;
  const isRedesignPromoPageEnabled = useIsRedesignPromoPageEnabled();
  const ctaPieces = isRedesignPromoPageEnabled
    ? heroCtaPartsCollection?.items
    : heroCtaPartsCollection?.items?.filter(
        (piece) =>
          piece?.presentation?.toLowerCase() === 'big button' ||
          piece?.pretranslated === 'price/time unit, cancel anytime'
      );
  const layoutConfig: ModuleLayoutConfig = {
    backgroundColor: unifiedModuleColors.blue,
  };
  const imgixProps = {
    src: logo.PLUS_FULL_WHITE_BLUE_PILL,
    alt: _t('Coursera Plus'),
  };

  const renderContent = () => (
    <div css={styles.contentContainer}>
      <Imgix {...imgixProps} width={285} />
      <Grid container justifyContent="center">
        <Grid item container sm={12} md={8} lg={8} css={styles.wrapper}>
          <div css={styles.typography(showFooterTextOnMobile ?? true)}>
            <FooterContentfulText />
          </div>
          <div css={styles.ctaContainer}>
            <CTAContentful ctaLocation={CTA_LOCATIONS.footer} ctaPieces={ctaPieces} />
          </div>
        </Grid>
      </Grid>
    </div>
  );
  return <ModuleLayout renderContent={renderContent} assembly={noImage(layoutConfig)} />;
};

export default CourseraPlusFooter;
